import jsSHA from 'jssha';
import MockSubject from 'models/MockSubject';
import judgementInfoCategories from 'constants/judgementInfoCategories';



const FETCH_TIMEOUT = 30000;
let DEFAULT_API_PARAMS = {
  nendo        : '',      // 設定ファイルから取得。 商品年度 - 入試年度ではなく年度なのでDN2019は「2018」年度更新
  gakunen      : '3',     // 商品学年 - 固定値「3」
  kai          : '8',     // 商品回 - 固定値「8」
  schoolCd     : '99999', // 学校CD - 固定「99999」
  moshiSiboList: [], // これは空配列を渡さないとダメっぽい、、、
};
const NEED_FIRST_CHOICE_MAP = {
  soci: {groupId: '1_2'}, // 地歴公民
  scie: {groupId: '6'  , uses: ['513', '523', '533', '543']}, // 理科
};

// ここで定義されている科目についてはパラメータ生成の際、
// 他の科目の得点を参照したり後処理をする
const MARK_INFO_RELATION_MAP = {
  '224': {targets: ['902', '903']},
  '301': {targets: ['911', '912', '913', '914']},
};

export default class JudgementInfo {
  static subjectCategories = judgementInfoCategories;

  static createCacheHashByParams(params) {
    const sha = new jsSHA('SHA-1', 'TEXT');
    sha.update(JSON.stringify(params));
    return sha.getHash('HEX');
  }



  get mockIsReady() { return this.mockState === 'ready' }
  get mockIsNotReady() { return this.mockState === 'notready' }
  get selfcheckIsReady() { return this.selfcheckState === 'ready' }
  get selfcheckIsNotReady() { return this.selfcheckState === 'notready' }
  get firstChoiceIsReady() { return this.firstChoiceState === 'ready' }
  get firstChoiceIsNotReady() { return this.firstChoiceState === 'notready' }
  get isReady() { return this.mockIsReady && this.selfcheckIsReady && this.firstChoiceIsReady }



  /**
   * constructor
   */
  constructor(year = dn.$store.getters['config/year']) {
    this.year = year;

    this.subjectCategories = judgementInfoCategories;
    this.mock              = new MockSubject();
    this.subjectsParams    = null;
    this.cacheHash         = null;
    this.mockState         = 'pending';
    this.selfcheckState    = 'pending';
    this.firstChoiceState  = 'pending';
    this.firstChoiceErrors = [];

    const debugQuery = dn.$store.state.route.query.jidebug;
    this.isDebug = debugQuery !== undefined;
    this.isDebugError = debugQuery === 'error';
    this.isDebugSleep = debugQuery ? !!(debugQuery.match(/^\d+$/)) : false;
    this.debugSleepAmmount = this.isDebugSleep ? parseFloat(debugQuery) : 0;
    this.useCache = !this.isDebug;
  }

  /**
   * 初期化
   */
  async init() {
    await Promise.all([
      this.mock.load(),
      dn.$store.dispatch('selfcheck/load'),
    ]);
    await this.updateHistories();
  }

  async updateHistories() {
    this.mock.restoreHistories();
    this.updateSubjectsParams();
  }

  getApiParams() {
    const cache = this.getCache();
    const schoolChoices = dn.segments.schoolChoices.find();
    const cmpsSiboList = [];

    schoolChoices.forEach(choice => {
      if (this.useCache) {
        if (!cache.judgements[choice]) cmpsSiboList.push({bosyutaniCd: choice});
      } else {
        cmpsSiboList.push({bosyutaniCd: choice});
      }
    });

    if (!cmpsSiboList.length) {
      return 'cacheonly';
    }

    return Object.assign(
      DEFAULT_API_PARAMS,
      this.subjectsParams,
      {cmpsSiboList}
    );
  }

  async fetch() {
    if (!this.isReady) {
      console.warn(this);
      throw new Error('APIリクエストの準備が完了していません。');
    }

    // 判定APIに渡す年度情報を設定ファイルから取得
    DEFAULT_API_PARAMS.nendo = dn.$store.getters['config/judgementApiNendo'];

    const params = this.getApiParams();

    if (params !== 'cacheonly') {
      await dn.$store.dispatch('config/ready');

      const url = dn.$store.getters['config/judgementApiUrl'];

      if (this.isDebug) {
        console.group('判定API送信データ');
        console.log(JSON.stringify(params, null, '  '));
        console.groupEnd();
      }

      if (this.isDebugError) {
        throw new Error('URLパラメータでエラー設定が行われているので、強制的に失敗します');
      }

      const promiseList = [dn.$http.post(url, {
        timeout    : FETCH_TIMEOUT,
        dataType   : 'json',
        contentType: 'application/json; charset=utf-8',
        data       : JSON.stringify(params),
      })];

      if (this.isDebugSleep) {
        console.log(`デバッグ値が設定されているので最低${this.debugSleepAmmount}秒スリープします`);
        promiseList.push(dn.$dn.sleep(this.debugSleepAmmount * 1000));
      }

      const res = (await Promise.all(promiseList))[0].data;

      if (this.isDebug) {
        console.group('判定APIレスポンスデータ');
        console.log(JSON.stringify(res, null, '  '));
        console.groupEnd();
      }

      if (res.result !== 0) {
        const err = new Error('判定APIのレスポンスが不正です');
        err.name = 'dn-judgement-api-error';
        throw new Error(err);
      }

      this.setCache(res.cmpsSiboList);
    }

    const cache = this.getCache();
    return cache.judgements;
  }

  setCache(cmpsSiboList) {
    if (dn.$store.getters['config/isManabi']) {
      const cache = this.getCache();
      cmpsSiboList.forEach(row => {
        const rtnStatusCd = row.rtnStatusCd;
        if (rtnStatusCd !== '50' && rtnStatusCd !== '00' && rtnStatusCd !== '000' && rtnStatusCd !== '050') {
          console.warn(row);
          throw new Error('Compass志望校情報のレスポンスが不正です');
        }

        const judgement = {
          isMissing: row.rtnStatusCd === '50',
          bosyutaniCd: row.bosyutaniCd,
          allentFlg: row.allentFlg,
          centerFlg: row.centerFlg,
          deciDK: {
            rank: row.deciDK.rank,
            dev: row.deciDK.dev,
            rankABase: row.deciDK.rankABase,
            rankBBase: row.deciDK.rankBBase,
            rankCBase: row.deciDK.rankCBase,
            rankDBase: row.deciDK.rankDBase,
            rankUpRank: row.deciDK.rankUpRank,
            rankUpDev: row.deciDK.rankUpDev,
          },
          deciList: [],
        };
        for (let i = 0; i < 2; i++) {
          const deci = row.deciList[i];
          judgement.deciList.push({
            rank: deci.rank,
            dev: deci.dev,
            rankABase: deci.rankABase,
            rankBBase: deci.rankBBase,
            rankCBase: deci.rankCBase,
            rankDBase: deci.rankDBase,
            rankUpRank: deci.rankUpRank,
            rankUpScore: deci.rankUpScore,
            japSelSubj: deci.japSelSubj,
            mathSelSubj: deci.mathSelSubj,
            engSelSubj: deci.engSelSubj,
            sociSelSubj: deci.sociSelSubj,
            scieSelSubj: deci.scieSelSubj,
            biko: deci.biko,
            niDankai: deci.niDankai,
          });
        }
        cache.judgements[row.bosyutaniCd] = judgement;
      });
      dn.$ls.set('judgement_cache', cache);
      return cache;
    } else {
    const cache = this.getCache();
    cmpsSiboList.forEach(row => {
      if (row.rtnStatusCd !== '50' && row.rtnStatusCd !== '00') {
        console.warn(row);
        throw new Error('Compass志望校情報のレスポンスが不正です');
      }

      const judgement = {
        isMissing: row.rtnStatusCd === '50',
        bosyutaniCd: row.bosyutaniCd,
        allentFlg: row.allentFlg,
        deciDK: {
          rank: row.deciDK.rank,
          rankUpRank: row.deciDK.rankUpRank,
          rankUpDev: row.deciDK.rankUpDev,
        },
        deciList: [],
      };
      for (let i = 0; i < 2; i++) {
        const deci = row.deciList[i];
        judgement.deciList.push({
          rank: deci.rank,
          dev: deci.dev,
          rankABase: deci.rankABase,
          rankBBase: deci.rankBBase,
          rankCBase: deci.rankCBase,
          rankDBase: deci.rankDBase,
          rankUpRank: deci.rankUpRank,
          rankUpScore: deci.rankUpScore,
          japSelSubj: deci.japSelSubj,
          mathSelSubj: deci.mathSelSubj,
          engSelSubj: deci.engSelSubj,
          sociSelSubj: deci.sociSelSubj,
          scieSelSubj: deci.scieSelSubj,
          biko: deci.biko,
          niDankai: deci.niDankai,
        });
      }
      cache.judgements[row.bosyutaniCd] = judgement;
    });
    dn.$ls.set('judgement_cache', cache);
    return cache;
  }
  }

  getCache() {
    const cache = JSON.parse(JSON.stringify(dn.$ls.get('judgement_cache', {
      hash: this.cacheHash,
      judgements: {},
    })));

    if (cache.hash !== this.cacheHash) {
      return this.initializeCache();
    }

    return cache;
  }

  updateCacheHash() {
    this.cacheHash = this.constructor.createCacheHashByParams(this.subjectsParams);
    const cache = JSON.parse(JSON.stringify(dn.$ls.get('judgement_cache', {})));
    if (cache.hash !== this.cacheHash) {
      this.initializeCache(this.cacheHash);
    }
  }

  initializeCache(hash) {
    const cache = {
      hash,
      judgements: {},
    };
    dn.$ls.set('judgement_cache', cache);
    return cache;
  }



  updateSubjectsParams() {
    this.mockState        = 'notready';
    this.selfcheckState   = 'notready';
    this.firstChoiceState = 'notready';
    this.subjectsParams   = this.getSubjectsParams();

    this.updateCacheHash();

    // 模試科目は1つも送信しないでもOKなので常にready
    this.mockState = 'ready';

    this.firstChoiceErrors = [];
    for (let key in NEED_FIRST_CHOICE_MAP) {
      let selects = this.subjectsParams.markInfoScore[key];
      if (NEED_FIRST_CHOICE_MAP[key].uses) {
        selects = selects.filter(select => NEED_FIRST_CHOICE_MAP[key].uses.includes(select.subjCd));
      }

      if (!selects.length) continue;

      const groupId = NEED_FIRST_CHOICE_MAP[key].groupId;
      const choice  = dn.$store.getters['selfcheck/groupedCategoryFirstChoice'](groupId);
      if (!choice) this.firstChoiceErrors.push({
        key: key,
        groupId: groupId,
        name: dn.$store.getters['selfcheck/groupedCategoryById'](groupId).name,
      });
    }
    if (!this.firstChoiceErrors.length) {
      this.firstChoiceState = 'ready';
    }

    for (let key in this.subjectsParams.markInfoScore) {
      if (this.subjectsParams.markInfoScore[key].length) {
        this.selfcheckState = 'ready';
        break;
      }
    }
  }



  getSubjectsParams() {
    const descInformations = this.getDescInformations();
    const markInfoScore = this.getMarkInfoScore();
    const params = Object.assign({
      markInfoScore: markInfoScore,
    }, descInformations);
    return params;
  }



  sortApiParamsSubjects(subjects) {
    subjects.sort((a, b) => {
      return parseInt(a.subjCd) > parseInt(b.subjCd) ? 1 : -1;
    });
  }



  getRelationMarkInfoScore(originId) {
    let relationScore;
    const mapping = MARK_INFO_RELATION_MAP[originId];
    if (mapping) {
      const ids = [originId, ...mapping.targets];
      for (let id of ids) {
        const history = dn.$store.getters['selfcheck/subjectHistory'](id);
        const score   = !history.selected || !history.fixed ? null : history.score;
        if (score) {
          relationScore = {
            id   : id,
            score: score,
          }
          break;
        }
      }
    }

    if (!relationScore) {
      const history = dn.$store.getters['selfcheck/subjectHistory'](originId);
      const score   = !history.selected || !history.fixed ? null : history.score;
      relationScore = {
        id   : originId,
        score: score,
      }
    }
    return relationScore;
  }



  getMarkInfoScore() {
    const markInfoScore = {};

    /**
     * リスニング得点一時変数
     * 英語以外の外国語を選択していた際に、2で割って四捨五入された得点がループの中で代入される
     * 後続のリスニング得点算出用に利用する
     */
    let listeningScore = null;

    this.subjectCategories.forEach(category => {
      markInfoScore[category.id] = [];

      category.center.forEach(id => {
        if (id === '311' && listeningScore !== null) {

          markInfoScore[category.id].push({subjCd: id, score: String(listeningScore)});
          return;
        }

        const relation = this.getRelationMarkInfoScore(id);
        let score = relation.score;
        if (score !== null) {

          /**
           * @MEMO
           * 英語以外の外国語を採用済みのときはその点数からリーディング＆リスニング得点を設定する
           * 2021年度の改定にあたって、リスニング含め英語の満点値が変更→200, 50から 100, 100となる
           */
          if (id === '301' && relation.id !== '301') {
            // >>> 判定送信対象が英語、かつ取ってきた得点が英語じゃない外国語だった時
            // 自身のスコアを2で割り＆四捨五入しておき、後続ループで発生するリスニングスコアを代入しておく
            const originalScore = score;
            score = Math.round(score / 2);
            listeningScore = originalScore - score;
          }
          markInfoScore[category.id].push({subjCd: id, score: String(score)});
        }
      });

      this.sortApiParamsSubjects(markInfoScore[category.id]);

      if (NEED_FIRST_CHOICE_MAP[category.id]) {
        const groupId = NEED_FIRST_CHOICE_MAP[category.id].groupId;
        const choice  = dn.$store.getters['selfcheck/groupedCategoryFirstChoice'](groupId);

        if (choice) {
          markInfoScore[category.id].sort((a, b) => { return a.subjCd === choice ? -1 : 1 });
        }
      }
    });

    return markInfoScore;
  }



  getDescInformations() {
    const info = {
      descInfo     : {},
      descInfoScore: {},
    };

    this.subjectCategories.forEach(category => {
      info.descInfo[category.id] = [];
      info.descInfoScore[category.id] = [];

      category.mock.forEach(id => {
        const history = this.mock.histories[id];
        if (history.score === null) return;
        info.descInfo[category.id].push({subjCd: id, score: String(Math.round(history.deviation * 10))});
        info.descInfoScore[category.id].push({subjCd: id, score: String(history.score)});
      });

      this.sortApiParamsSubjects(info.descInfo[category.id]);
      this.sortApiParamsSubjects(info.descInfoScore[category.id]);
    });

    return info;
  }
}



// WEBPACK FOOTER //
// ./src/js/models/judgementInfo/JudgementInfo.js